import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { ROUTE_LABELS, ROUTE_LABEL_KEYS } from '@/lib/routes';
import { useT } from '@/lib/i18n';

// VF.2.a — détection segment UUID v4 (id de ressource dynamique)
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Breadcrumb — génération automatique depuis pathname.
 *
 * Ex: /flashinfo/feed → Flash Info > Feed
 *     /querybuilder/lists/abc → Query Builder > Listes sauvegardées > abc
 *     /flashinfo/edit/<uuid> → Flash Info > Édition > Détail (au lieu de l'UUID brut)
 *
 * ⚠ L'EXEMPLE CITAIT `/flashinfo/<uuid>` — CETTE ROUTE N'EXISTE PLUS (supprimée,
 *   décision Hkm : la fiche s'ouvre en overlay). Seul le COMMENTAIRE était mort :
 *   `UUID_REGEX` ci-dessous reste VIVANT, car `/flashinfo/edit/<uuid>` porte lui
 *   aussi un segment UUID. Le retirer afficherait un UUID brut dans le fil
 *   d'Ariane de l'édition.
 */
export function Breadcrumb() {
  const { pathname } = useLocation();
  const t = useT();

  // Structure (segments + chemins) mémoïsée sur le pathname ; le libellé est
  // résolu au rendu pour suivre la bascule de langue (`#796`).
  const segs = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    const result: Array<{ seg: string; path: string; isLast: boolean }> = [];
    let cumulated = '';
    segments.forEach((seg, i) => {
      cumulated += `/${seg}`;
      result.push({ seg, path: cumulated, isLast: i === segments.length - 1 });
    });
    return result;
  }, [pathname]);

  const crumbs = segs.map(({ seg, path, isLast }) => {
    // 1. Clé catalogue (Δ=0 FR) · 2. ROUTE_LABELS littéral · 3. « Détail » (UUID) · 4. segment brut
    const cle = ROUTE_LABEL_KEYS[path];
    const label = cle ? t(cle)
      : ROUTE_LABELS[path] ?? (UUID_REGEX.test(seg) ? 'Détail' : seg);
    return { label, path, isLast };
  });

  if (crumbs.length === 0) return null;

  return (
    <nav aria-label={t('commun.filAriane')} className="flex items-center gap-1 text-sm">
      {crumbs.map((crumb, idx) => (
        <span key={crumb.path} className="flex items-center gap-1">
          {idx > 0 && (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          )}
          {crumb.isLast ? (
            <span className="font-medium text-foreground">{crumb.label}</span>
          ) : (
            <Link
              to={crumb.path}
              className="text-muted-foreground transition-colors hover:text-foreground"
            >
              {crumb.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
}
